import {FC, FormEvent, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import clsx from 'clsx'
import {
  CheckoutOptions,
  GatewayOption,
  goToGateway,
  parseAmount,
  payableFor,
  readApiError,
  rial,
  StartedPayment,
  StartPaymentInput,
} from '../core/onlinePaymentApi'
import {AmountInput, AmountWords} from '../../apps/shared/amount/AmountInput'
import {GatewayPicker} from './GatewayPicker'
import './OnlinePayment.scss'

const toPersianDigits = (value: string) => value.replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])

/** The amount field's text: Persian digits with grouping, as the payer types. */
const formatInput = (value?: number) => (value === undefined ? '' : toPersianDigits(value.toLocaleString('en-US')).replace(/,/g, '٬'))

/** How long the payer reads «… در دسترس نبود؛ پرداخت با … ادامه می‌یابد» before the bank's page opens. */
export const NOTICE_DELAY_MS = 2500

export type OnlinePaymentFormProps = {
  options: CheckoutOptions
  /** Starts the payment on the server (invoice hash or buyer link) and answers where the browser goes. */
  start: (input: StartPaymentInput) => Promise<StartedPayment>
  defaultMobile?: string | null
  onCancel?: () => void
  /** Reset when the dialog opens again. */
  active?: boolean
  /** Wraps the fields and the buttons (the dialog puts them in its body and footer). */
  layout?: (fields: JSX.Element, buttons: JSX.Element, busy: boolean) => JSX.Element
  /** Told while the payer is on the way to the gateway (the dialog then cannot be closed). */
  onBusyChange?: (busy: boolean) => void
}

export type OnlinePaymentDialogProps = Omit<OnlinePaymentFormProps, 'onCancel' | 'active' | 'layout' | 'onBusyChange'> & {
  show: boolean
  onHide: () => void
}

/**
 * «پرداخت آنلاین»: the business's gateways, the amount (lowerable down to the business's minimum when it allows
 * it, never above what is left), the payer's mobile for saved cards, then the gateway. The server checks every
 * value again; this form only helps the payer get it right the first time.
 */
export const OnlinePaymentForm: FC<OnlinePaymentFormProps> = ({
  options,
  start,
  defaultMobile,
  onCancel,
  active = true,
  layout,
  onBusyChange,
}) => {
  const [gatewayId, setGatewayId] = useState<number | undefined>(options.gateways[0]?.id)
  const [amountText, setAmountText] = useState(formatInput(options.remaining))
  const [mobile, setMobile] = useState(defaultMobile || '')
  const [busy, setBusyState] = useState(false)
  const [error, setError] = useState('')
  // Another gateway took the payment (the chosen one refused): said for a moment before the page leaves.
  const [notice, setNotice] = useState('')
  const setBusy = (value: boolean) => {
    setBusyState(value)
    onBusyChange?.(value)
  }

  useEffect(() => {
    if (active) {
      setGatewayId(options.gateways[0]?.id)
      setAmountText(formatInput(options.remaining))
      setError('')
      setNotice('')
      setBusyState(false)
    }
  }, [active, options])

  // «فقط خودکار» (2026-10-18): no choice for the payer; the business's order decides and the server passes a refused
  // payment on to the next gateway.
  const automatic = options.gatewayChoice === 'automatic' && options.gateways.length > 1
  const gateway: GatewayOption | undefined = automatic ? undefined : options.gateways.find((item) => item.id === gatewayId)
  const typed = options.allowCustomAmount ? parseAmount(amountText) : options.remaining
  const minimum = Math.max(options.minimum, gateway?.minimumAmount || 0)
  const amountError = useMemo(() => {
    if (!options.allowCustomAmount) return ''
    if (typed === undefined) return 'مبلغ را به ریال و فقط با رقم وارد کنید.'
    if (typed > options.remaining) return `مبلغ نمی‌تواند بیشتر از مانده (${rial(options.remaining)} ریال) باشد.`
    if (typed < Math.min(minimum, options.remaining)) return `کمترین مبلغ پرداخت ${rial(Math.min(minimum, options.remaining))} ریال است.`
    if (gateway?.maximumAmount && typed > gateway.maximumAmount)
      return `بیشترین مبلغ یک پرداخت در ${gateway.title} ${rial(gateway.maximumAmount)} ریال است؛ مبلغ را در چند پرداخت بپردازید.`
    return ''
  }, [options, typed, minimum, gateway])
  const payable = typed === undefined ? undefined : payableFor(typed, gateway)
  const mobileDigits = mobile.replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace(/\D/g, '')
  const mobileError = mobileDigits && !/^09\d{9}$/.test(mobileDigits) ? 'شمارهٔ همراه باید ۱۱ رقم و با ۰۹ شروع شود.' : ''
  const canPay = (automatic || !!gateway) && payable !== undefined && payable > 0 && !amountError && !mobileError && !busy

  const pay = async (event: FormEvent) => {
    event.preventDefault()
    if (!canPay || (!automatic && !gateway) || typed === undefined) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const started = await start({
        gatewayId: automatic ? undefined : gateway?.id,
        amount: options.allowCustomAmount ? typed : undefined,
        payerMobile: mobileDigits || undefined,
      })
      if (started.notice) {
        // The payer chose one gateway and lands on another: say so before the bank's page opens.
        setNotice(started.notice)
        window.setTimeout(() => goToGateway(started.redirect), NOTICE_DELAY_MS)
      } else {
        goToGateway(started.redirect)
      }
      // The page is leaving for the gateway; the button stays busy until it does.
    } catch (startError) {
      setError(readApiError(startError, 'اتصال به درگاه انجام نشد. دوباره تلاش کنید یا درگاه دیگری را انتخاب کنید.').message)
      setBusy(false)
    }
  }

  const fields = (
    <>
          <dl className='op-summary'>
            <div>
              <dt>مبلغ کل</dt>
              <dd>{rial(options.total)} ریال</dd>
            </div>
            {options.paid > 0 && (
              <div>
                <dt>پرداخت‌شده</dt>
                <dd>{rial(options.paid)} ریال</dd>
              </div>
            )}
            <div className='op-summary__remaining'>
              <dt>مانده</dt>
              <dd>{rial(options.remaining)} ریال</dd>
            </div>
          </dl>

          {automatic ? (
            <p className='op-auto-note mb-5'>
              <i className='las la-random me-1' aria-hidden='true' />
              درگاه پرداخت به ترتیب فروشنده انتخاب می‌شود؛ اگر درگاهی در دسترس نباشد، پرداخت با درگاه بعدی ادامه
              می‌یابد.
            </p>
          ) : (
          <GatewayPicker
            className='mb-5'
            name='op-gateway'
            gateways={options.gateways}
            value={gatewayId}
            onChange={setGatewayId}
            disabled={busy}
            fallbackNote='اگر درگاه انتخاب‌شده در دسترس نباشد، پرداخت با درگاه بعدی فروشنده ادامه می‌یابد.'
          />
          )}

          <div className='mb-5'>
            <label className='form-label fw-bold' htmlFor='op-amount'>
              مبلغ پرداخت (ریال)
            </label>
            {options.allowCustomAmount ? (
              <>
                <div className='input-group'>
                  <AmountInput
                    id='op-amount'
                    className={clsx('form-control', {'is-invalid': !!amountError})}
                    value={amountText}
                    onValueChange={setAmountText}
                    disabled={busy}
                    aria-invalid={!!amountError}
                    aria-describedby='op-amount-hint'
                  />
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => setAmountText(formatInput(options.remaining))}
                    disabled={busy}
                  >
                    کل مانده
                  </button>
                </div>
                <div id='op-amount-hint' className={clsx('fs-7 mt-2', amountError ? 'text-danger' : 'text-muted')} role={amountError ? 'alert' : undefined}>
                  {amountError || `می‌توانید مبلغی بین ${rial(Math.min(minimum, options.remaining))} تا ${rial(options.remaining)} ریال بپردازید.`}
                </div>
                <AmountWords value={amountText} />
              </>
            ) : (
              <div id='op-amount' className='op-fixed-amount'>
                {rial(options.remaining)} ریال
              </div>
            )}
            {gateway?.amountInTomans && payable !== undefined && typed !== undefined && payable !== typed && (
              <div className='text-muted fs-7 mt-2'>
                {gateway.title} با تومان کار می‌کند؛ {rial(payable)} ریال پرداخت می‌شود.
              </div>
            )}
          </div>

          <div className='mb-2'>
            <label className='form-label' htmlFor='op-mobile'>
              شمارهٔ همراه <span className='text-muted fs-7'>(اختیاری؛ برای نمایش کارت‌های ذخیره‌شدهٔ شما در درگاه)</span>
            </label>
            <input
              id='op-mobile'
              className={clsx('form-control', {'is-invalid': !!mobileError})}
              inputMode='tel'
              autoComplete='tel'
              dir='ltr'
              placeholder='09xxxxxxxxx'
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              disabled={busy}
              aria-invalid={!!mobileError}
            />
            {mobileError && <div className='invalid-feedback d-block'>{mobileError}</div>}
          </div>

          {error && (
            <div className='alert alert-danger mt-4 mb-0' role='alert'>
              {error}
            </div>
          )}
          {notice && (
            <div className='alert alert-info mt-4 mb-0' role='status'>
              {notice}
            </div>
          )}
    </>
  )
  const buttons = (
    <>
          <span className='text-muted fs-7'>
            <i className='las la-lock me-1' aria-hidden='true' />
            پرداخت در صفحهٔ امن درگاه بانکی انجام می‌شود.
          </span>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-light' onClick={onCancel} disabled={busy}>
              انصراف
            </button>
            <button type='submit' className='btn btn-primary' disabled={!canPay}>
              {busy ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                  در حال انتقال به درگاه…
                </>
              ) : (
                <>پرداخت {payable !== undefined && !amountError ? `${rial(payable)} ریال` : ''}</>
              )}
            </button>
          </div>
    </>
  )

  return (
    <form onSubmit={pay} noValidate>
      {layout ? (
        layout(fields, buttons, busy)
      ) : (
        <>
          {fields}
          <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mt-5'>{buttons}</div>
        </>
      )}
    </form>
  )
}

/** The form in a dialog: the print pages' «پرداخت آنلاین». */
export const OnlinePaymentDialog: FC<OnlinePaymentDialogProps> = ({show, onHide, options, start, defaultMobile}) => {
  const [busy, setBusy] = useState(false)
  const kind = options.isProforma ? 'پیش‌فاکتور' : 'صورتحساب'
  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered dialogClassName='op-dialog' aria-labelledby='op-dialog-title'>
      <OnlinePaymentForm
        options={options}
        start={start}
        defaultMobile={defaultMobile}
        onCancel={onHide}
        active={show}
        onBusyChange={setBusy}
        layout={(fields, buttons, formBusy) => (
          <>
            <Modal.Header closeButton={!formBusy}>
              <Modal.Title id='op-dialog-title' as='h2' className='fs-3'>
                <i className='las la-credit-card text-primary me-2' aria-hidden='true' />
                پرداخت آنلاین {kind}
                {options.invoiceNo ? <bdi className='ms-1'>{options.invoiceNo}</bdi> : null}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>{fields}</Modal.Body>
            <Modal.Footer className='justify-content-between'>{buttons}</Modal.Footer>
          </>
        )}
      />
    </Modal>
  )
}
